import { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import api from '../services/api'

// Read the reset token from the URL fragment (`#t=<token>`) rather than
// the query string.  Fragments are never sent to the server, never land
// in Apache access logs, never leak via Referer.  Persist to sessionStorage
// on first read so an accidental refresh doesn't strand the user (matches
// the EngagementSign.js pattern).
function readTokenFromHashOrStorage() {
  try {
    const raw = window.location.hash || ''
    const match = raw.match(/(?:^|[#&])t=([^&]+)/)
    if (match && match[1]) {
      const t = decodeURIComponent(match[1])
      try { sessionStorage.setItem('lextrack_pwreset_token', t) } catch { /* private mode */ }
      // Scrub the fragment out of the URL bar so it doesn't sit in
      // browser history / bookmarks / window title.
      try { window.history.replaceState(null, '', window.location.pathname) } catch { /* noop */ }
      return t
    }
    return sessionStorage.getItem('lextrack_pwreset_token') || ''
  } catch {
    return ''
  }
}

export default function ResetPassword() {
  const navigate = useNavigate()
  const token = useMemo(readTokenFromHashOrStorage, [])

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [retryAfter, setRetryAfter] = useState(0)
  const [successPayload, setSuccessPayload] = useState(null)
  const [loading, setLoading] = useState(false)

  // Only redirect after showing the success message long enough for the
  // user to READ the 2FA warning if they have one.  Users without 2FA
  // get the short 2s redirect matching the original UX; 2FA users get
  // no auto-redirect at all so they see the warning before continuing.
  useEffect(() => {
    if (!successPayload) return
    if (successPayload.totp_enabled) return // no auto-redirect for 2FA users
    const t = setTimeout(() => {
      // Clear the sessionStorage token now that we're done — one-shot.
      try { sessionStorage.removeItem('lextrack_pwreset_token') } catch { /* noop */ }
      navigate('/login')
    }, 2500)
    return () => clearTimeout(t)
  }, [successPayload, navigate])

  const validate = () => {
    if (!token) return 'Missing reset token — request a new link.'
    if (password.length < 8) return 'Password must be at least 8 characters.'
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      return 'Password must contain at least one letter and one number.'
    }
    if (password !== confirm) return 'Passwords do not match.'
    return null
  }

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setRetryAfter(0)
    const clientError = validate()
    if (clientError) { setError(clientError); return }
    setLoading(true)
    try {
      const { data } = await api.post('/api/auth/reset-password', { token, new_password: password })
      setSuccessPayload(data || { totp_enabled: false })
    } catch (err) {
      // Distinguish rate-limit responses (429) from other errors so we
      // can render a clear "wait N minutes" message with Retry-After.
      const status = err.response?.status
      const retryHeader = err.response?.headers?.['retry-after']
      if (status === 429) {
        const seconds = parseInt(retryHeader, 10) || 900
        setRetryAfter(seconds)
        setError(`You've hit the rate limit — please wait ${Math.ceil(seconds / 60)} minute${seconds >= 120 ? 's' : ''} and try again.`)
      } else {
        setError(err.response?.data?.error || 'Could not reset password. The link may have expired.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-10 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-600">Omnia Practice Management</h1>
          <p className="text-gray-500 mt-2">Reset Password</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 rounded-lg p-3 mb-6 text-sm" role="alert">
            {error}
            {retryAfter > 0 && (
              <div className="mt-1 text-xs text-red-500">
                Retry after: {Math.ceil(retryAfter / 60)} minute{retryAfter >= 120 ? 's' : ''}.
              </div>
            )}
          </div>
        )}

        {!token && !successPayload ? (
          <div className="text-center">
            <div className="bg-amber-50 text-amber-700 rounded-lg p-4 mb-6 text-sm">
              Missing reset token — request a new link.
            </div>
            <Link to="/forgot-password" className="text-primary-600 hover:underline text-sm font-medium">
              Request password reset →
            </Link>
          </div>
        ) : successPayload ? (
          <div className="text-center">
            <div className="bg-green-50 text-green-700 rounded-lg p-4 mb-4 text-sm">
              <div className="font-medium mb-1">Password updated.</div>
              {!successPayload.totp_enabled && <div>Redirecting to sign in…</div>}
            </div>
            {successPayload.totp_enabled && (
              <div className="bg-amber-50 text-amber-800 rounded-lg p-4 mb-4 text-sm text-left">
                <div className="font-semibold mb-1">🔐 Two-factor authentication reminder</div>
                <p className="mb-2">
                  Your account has 2FA enabled. You'll still need your authenticator
                  app (or a backup code) to complete sign-in.
                </p>
                <p className="text-xs text-amber-700">
                  Lost your authenticator? Contact your firm administrator — they can
                  reset your 2FA enrollment.
                </p>
              </div>
            )}
            {successPayload.revoke_succeeded === false && (
              <div className="bg-amber-50 text-amber-800 rounded-lg p-3 mb-4 text-xs text-left">
                <strong>Note:</strong> we couldn't immediately sign out other devices
                that were using your old password. After signing in, change your
                password again from Profile so all sessions clear.
              </div>
            )}
            {successPayload.confirmation_email_sent === false && (
              <div className="bg-amber-50 text-amber-800 rounded-lg p-3 mb-4 text-xs text-left">
                <strong>Note:</strong> the confirmation email couldn't be delivered
                right now. Your password change is in effect; contact your
                administrator if you didn't expect this reset.
              </div>
            )}
            <Link
              to="/login"
              onClick={() => { try { sessionStorage.removeItem('lextrack_pwreset_token') } catch { /* noop */ } }}
              className="text-primary-600 hover:underline text-sm font-medium"
            >
              Sign in →
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <p className="text-sm text-gray-600">
              Choose a new password for your account.
            </p>
            <div className="bg-blue-50 text-blue-800 rounded-lg p-3 text-xs">
              <strong>Two-factor authentication note:</strong> resetting your password
              does <em>not</em> clear your 2FA enrollment. If you had 2FA on before,
              you'll still be prompted for a 6-digit code at sign-in.
            </div>
            <div>
              <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                id="reset-password" name="new-password" type="password"
                autoComplete="new-password" required autoFocus
                value={password} onChange={e => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5
                           focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="••••••••"
              />
              <p className="text-xs text-gray-400 mt-1">
                At least 8 characters, including one letter and one number.
              </p>
            </div>
            <div>
              <label htmlFor="reset-confirm" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                id="reset-confirm" name="confirm-password" type="password"
                autoComplete="new-password" required
                value={confirm} onChange={e => setConfirm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5
                           focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit" disabled={loading || !password || !confirm}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white
                         font-semibold rounded-lg py-2.5 transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Resetting…' : 'Reset Password'}
            </button>
            <div className="text-center text-sm">
              <Link to="/login" className="text-gray-500 hover:underline">
                ← Back to sign in
              </Link>
            </div>
          </form>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Omnia Practice Management © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  )
}
